import { z } from 'zod/v4';
import { trpc } from '@org/clients/school';
import { DynamicForm } from '@org/ui';
import { toast } from 'sonner';
import { useState, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { unwrapTrpcData } from '@org/ui';
import { classUpdateSchema } from '@org/model';
import { LazyMedia } from '@org/ui';
import { MediaUploader } from '@org/ui';
import type { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type ClassFormValues = z.infer<typeof classUpdateSchema>;
type CustomRendererContext = {
  field: ControllerRenderProps<ClassFormValues>;
  error?: string;
  methods: UseFormReturn<ClassFormValues>;
};

// Define a ClassFormRef type for use with forwardRef
export interface ClassFormRef {
  submit: () => void;
}

type ClassFormProps = {
  defaultValues: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
  hideButtons?: boolean;
  /** Edits vs. the defaults (lets the multi-step form guard a step change). */
  onDirtyChange?: (dirty: boolean) => void;
};

export const ClassForm = forwardRef<ClassFormRef, ClassFormProps>(({
  defaultValues,
  onSubmit,
  onClose,
  hideButtons,
  onDirtyChange
}, ref) => {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const classId = defaultValues?.classId;
  const { data: levels } = trpc.levels.list.useQuery();

  const levelOptions = useMemo(
    () => unwrapTrpcData(levels) ?? [],
    [levels]
  );
  
  const [formDefaultValues, setFormDefaultValues] = useState(defaultValues);
  const formRef = useRef<any>(null);

  const createMutation = trpc.classes.create.useMutation({
    onSuccess: () => utils.classes.list.invalidate()
  });

  const updateMutation = trpc.classes.update.useMutation({
    onSuccess: () => utils.classes.list.invalidate()
  });

  // Expose the submit method to the parent component
  useImperativeHandle(ref, () => ({
    submit: () => {
      if (formRef.current) {
        // Trigger the form submission programmatically
        formRef.current.requestSubmit();
      }
    }
  }));

  const handleSubmit = async (data: any) => {
    try {
      let savedClass;
      if (classId) {
        // For update
        savedClass = await updateMutation.mutateAsync({
          id: classId,
          data: {
            name: data.name,
            description: data.description,
            imageUrl: data.imageUrl,
            levelId: data.levelId
          }
        });
        toast.success(t('common.toasts.updateSuccess', { entity: t('common.fallbacks.class') }));
      } else {
        // For create
        savedClass = await createMutation.mutateAsync({
          name: data.name,
          description: data.description,
          imageUrl: data.imageUrl,
          levelId: data.levelId
        });
        toast.success(t('common.toasts.createSuccess', { entity: t('common.fallbacks.class') }));
      }
      onSubmit({
        ...data,
        classId: savedClass?.classId ?? classId
      });
    } catch (error) {
      console.error('Error submitting class:', error);
      toast.error(t('common.toasts.actionError', { action: t('common.actions.update').toLowerCase(), entity: t('common.fallbacks.class').toLowerCase() }));
    }
  };

  const labelMappings = {
    name: t('classes.form.labels.name'),
    description: t('common.labels.description'),
    imageUrl: t('classes.form.labels.image'),
    levelId: t('common.labels.level'),
  };

  const foreignKeyOptions = {
    levels: levelOptions.map((level: any) => ({
      id: level.levelId,
      label: level.name,
    })),
  };

  // Define fields to hide from the form
  const hiddenFields = ['classId'];

  // Custom renderer for imageUrl using LazyMedia
  const customRenderers: Record<string, (context: CustomRendererContext) => React.ReactNode> = {
    imageUrl: ({ field }) => {
      const { value, onChange } = field;
      if (value) {
        // Show image preview with LazyMedia
        return (
          <LazyMedia
            title={t('classes.form.media.previewTitle')}
            placeholder={t('classes.form.media.previewPlaceholder')}
            onReplace={() => onChange('')}
          >
            <div className="aspect-video bg-muted rounded-md overflow-hidden max-w-md">
              <img
                src={value}
                alt={t('classes.form.media.previewAlt')}
                className="object-contain w-full h-full"
              />
            </div>
          </LazyMedia>
        );
      }

      return (
        <MediaUploader
          compact
          acceptedFileTypes={["image/jpeg", "image/png", "image/gif", "image/webp"]}
          title={t('classes.form.media.uploadTitle')}
          description={t('classes.form.media.uploadDescription')}
          onUploadComplete={(url) => onChange(url)}
        />
      );
    }
  };

  return (
    <DynamicForm
      schema={classUpdateSchema}
      onSubmit={handleSubmit}
      defaultValues={formDefaultValues}
      isEditing={!!classId}
      onClose={onClose}
      labelMappings={labelMappings}
      hiddenFields={hiddenFields}
      hideButtons={hideButtons}
      formRef={formRef}
      foreignKeyOptions={foreignKeyOptions}
      customRenderers={customRenderers}
      onDirtyChange={onDirtyChange}
    />
  );
}); 